import { supabase } from '../lib/supabase';

interface Registration {
  student_id: string;
  event_id: string;
  competition_year: number;
  status: 'registered' | 'confirmed' | 'withdrawn' | 'disqualified';
}

export const getRegistrationsByEvent = async (eventId: string) => {
  const { data, error } = await supabase
    .from('student_event_registrations')
    .select(`
      *,
      student:students(
        first_name,
        last_name,
        first_name_tamil,
        last_name_tamil,
        date_of_birth,
        house:houses(
          house_name,
          house_color
        )
      )
    `)
    .eq('event_id', eventId)
    .order('created_at', { ascending: true });

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

export const createRegistration = async (registration: Registration) => {
  try {
    // First check if this is a team event
    const { data: eventData, error: eventError } = await supabase
      .from('events')
      .select('is_team_event')
      .eq('event_id', registration.event_id)
      .single();

    if (eventError) {
      throw new Error(eventError.message);
    }

    // Only check registration limit for individual events
    if (!eventData.is_team_event) {
      // Check existing registrations count for this student in the current year
      // Only count individual events (not team events)
      const { data: individualEvents } = await supabase
        .from('events')
        .select('event_id')
        .eq('is_team_event', false);
        
      const individualEventIds = individualEvents?.map(e => e.event_id) || [];
      
      if (individualEventIds.length > 0) {
        const { count, error: countError } = await supabase
          .from('student_event_registrations')
          .select('registration_id', { count: 'exact', head: true })
          .eq('student_id', registration.student_id)
          .eq('competition_year', registration.competition_year)
          .neq('status', 'withdrawn')
          .in('event_id', individualEventIds);

        if (countError) {
          throw new Error(countError.message);
        }

        if ((count || 0) >= 3) {
          // Get student details to include in error message
          const { data: studentData, error: studentError } = await supabase
            .from('students')
            .select('first_name, last_name')
            .eq('student_id', registration.student_id)
            .single();

          if (studentError) {
            throw new Error('Student cannot register for more than 3 individual competitions per year');
          }

          const fullName = `${studentData.first_name} ${studentData.last_name}`;
          throw new Error(`${fullName} has already registered for the maximum allowed number of individual competitions (3) this year`);
        }
      }
    }

    // Insert the registration
    const { data, error } = await supabase
      .from('student_event_registrations')
      .insert([registration])
      .select(`
        registration_id,
        student_id,
        event_id,
        competition_year,
        status,
        created_at,
        student:students(
          first_name,
          last_name,
          date_of_birth,
          house:houses(
            house_name,
            house_color
          )
        ),
        event:events(
          event_name,
          age_group:age_groups(
            group_name,
            birth_years
          )
        )
      `)
      .single();
      
    if (error) {
      // Extract the age group validation error message
      if (error.message.includes('not in the allowed age group years')) {
        throw new Error('Participant\'s age does not match the event\'s age group requirements');
      }
      throw new Error(error.message);
    }
    
    return data;
  } catch (error) {
    // Ensure we always throw an Error object with a message
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred during registration');
  }
};

export const updateRegistrationStatus = async (registrationId: string, status: Registration['status']) => {
  const { data, error } = await supabase
    .from('student_event_registrations')
    .update({ status })
    .eq('registration_id', registrationId)
    .select()
    .single();
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data;
};

export const updateMassRegistrationStatus = async (eventId: string, status: Registration['status']) => {
  // Check if user has admin or manager role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators and managers can update all registrations');
  }

  const { data, error } = await supabase
    .from('student_event_registrations')
    .update({ status })
    .eq('event_id', eventId)
    .select();
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data;
};

export const deleteRegistration = async (registrationId: string) => {
  // Check if user has admin or manager role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators and managers can delete registrations');
  }

  // First check if there are any results for this registration
  const { data: registration, error: regError } = await supabase
    .from('student_event_registrations')
    .select('student_id, event_id')
    .eq('registration_id', registrationId)
    .single();
    
  if (regError) {
    throw new Error(regError.message);
  }
  
  // Check for individual results
  const { data: results, error: resultsError } = await supabase
    .from('competition_results')
    .select('result_id')
    .eq('event_id', registration.event_id)
    .eq('student_id', registration.student_id);
    
  if (resultsError) {
    throw new Error(resultsError.message);
  }
  
  if (results && results.length > 0) {
    throw new Error('Cannot delete registration: Results have already been recorded');
  }

  // Delete the registration
  const { error } = await supabase
    .from('student_event_registrations')
    .delete()
    .eq('registration_id', registrationId);
    
  if (error) {
    throw new Error(error.message);
  }
};

export const deleteTeamRegistration = async (eventId: string, houseId: string) => {
  // Check if user has admin or manager role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators and managers can delete team registrations');
  }

  // Check for team results
  const { data: teamResults, error: resultsError } = await supabase
    .from('team_results')
    .select('result_id')
    .eq('event_id', eventId)
    .eq('house_id', houseId);
    
  if (resultsError) {
    throw new Error(resultsError.message);
  }
  
  if (teamResults && teamResults.length > 0) {
    throw new Error('Cannot delete team registration: Results have already been recorded');
  }

  // Delete the team registration
  const { error } = await supabase
    .from('team_registrations')
    .delete()
    .eq('event_id', eventId)
    .eq('house_id', houseId);
    
  if (error) {
    throw new Error(error.message);
  }
};